import React, { Component, ErrorInfo, ReactNode } from 'react'
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { captureException } from '../lib/sentry'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call optional error handler (e.g., for Sentry reporting)
    this.props.onError?.(error, errorInfo)

    captureException(error, {
      component_stack: errorInfo.componentStack,
      boundary: 'global',
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-alt px-4">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto w-16 h-16 bg-danger-soft rounded-full flex items-center justify-center mb-6">
              <ExclamationTriangleIcon className="w-8 h-8 text-danger" />
            </div>

            <h1 className="text-2xl font-bold text-ink mb-2">
              Something went wrong
            </h1>

            <p className="text-ink-muted mb-6">
              We're sorry, but something unexpected happened. Please try again or contact support if the problem persists.
            </p>

            {/* Error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-danger-soft rounded-button text-left">
                <p className="text-sm font-mono text-danger break-all">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-sm text-danger cursor-pointer">
                      Stack trace
                    </summary>
                    <pre className="mt-2 text-xs text-danger overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-hairline rounded-button text-sm font-medium text-ink bg-surface hover:bg-surface-alt focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Try Again
              </button>

              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-button text-sm font-medium text-on-accent bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Reload Page
              </button>
            </div>

            <p className="mt-6 text-sm text-ink-muted">
              If this problem persists, please contact{' '}
              <a
                href="mailto:support@lipaflow.co.ke"
                className="text-accent hover:text-accent-hover"
              >
                support@lipaflow.co.ke
              </a>
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary


export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  fallback?: ReactNode
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    )
  }
}

/**
 * Specialized error boundary for onboarding flow
 * Provides onboarding-specific error recovery options
 */
export class OnboardingErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('OnboardingErrorBoundary caught an error:', error, errorInfo)

    this.props.onError?.(error, errorInfo)
    this.setState({ errorInfo })

    captureException(error, {
      component_stack: errorInfo.componentStack,
      boundary: 'onboarding',
      context: 'onboarding',
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-alt px-4">
          <div className="max-w-md w-full bg-surface rounded-button shadow-lg p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-ink text-center mb-2">
              Onboarding Error
            </h2>

            <p className="text-ink-muted text-center mb-6">
              We encountered an issue during onboarding. Don't worry - your progress has been saved.
              Please try again or contact support.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-surface-alt rounded-button">
                <p className="text-sm font-mono text-danger break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-xs text-ink-muted cursor-pointer">
                      Stack trace
                    </summary>
                    <pre className="mt-2 text-xs text-ink-muted overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full flex items-center justify-center px-4 py-3 bg-accent text-on-accent rounded-button hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
              >
                <ArrowPathIcon className="w-5 h-5 mr-2" />
                Continue Onboarding
              </button>

              <button
                onClick={() => (window.location.href = '/onboarding')}
                className="w-full px-4 py-3 border border-hairline text-ink rounded-button hover:bg-surface-alt focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Restart From Current Step
              </button>

              <button
                onClick={() => (window.location.href = '/')}
                className="w-full px-4 py-3 border border-hairline text-ink rounded-button hover:bg-surface-alt focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Go to Dashboard
              </button>

              <a
                href="mailto:support@lipaflow.co.ke?subject=Onboarding Error"
                className="text-center text-sm text-accent hover:text-accent-hover"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Hook to manually trigger error boundary
 * Useful for async errors that aren't caught by error boundaries
 *
 * Usage:
 * const handleError = useErrorHandler()
 * try {
 *   await someAsyncOperation()
 * } catch (error) {
 *   handleError(error)
 * }
 */
export function useErrorHandler() {
  const [, setError] = React.useState()

  return React.useCallback((error: Error) => {
    setError(() => {
      throw error
    })
  }, [])
}
